import { useEffect, useState } from "react";
import {
  Controller,
  FieldError,
  SubmitHandler,
  useForm,
} from "react-hook-form";
import toast from "react-hot-toast";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { CustomButton, FileUploadIcon, InputField, Sidebar } from "../../components";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import {
  useCreateBadges,
  useGetbadgesById,
  useUpdateBadges,
} from "../../api/useProducts/useBadges";
import useSingleFileUpload from "../../hook/useSingleFileUpload";
import moment from "moment";

export interface ChallengesFormData {
  discount: number;
  points: number;
  description: string;
  type: string;
  code: string;
  image: string;
  expiryDate:any
}

const UpdateBadges = () => {
  const { id } = useParams();
  const today = new Date().toISOString().split("T")[0];
  const { GetbadgesById, GetbadgesByIdLoading, GetbadgesByIdRefetch } =
    useGetbadgesById(Number(id));
  const { mutate, isPending } = useUpdateBadges(Number(id));

  const { uploadImage, isUploading } = useSingleFileUpload();

  const [uploadedData, setUploadedData] = useState<any>();

  

  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
  } = useForm<ChallengesFormData>();

  const selectedType = watch("type");
  // const selectedDiscount = watch("discount");


  useEffect(() => {
    if (GetbadgesById) {
      setValue("type", GetbadgesById?.type);
      setValue("description", GetbadgesById?.description);
      setValue("discount", GetbadgesById?.discount);
      setValue("code", GetbadgesById?.coupon_code);
      setValue("points", GetbadgesById?.target_points);
      setValue(
        "expiryDate",
        moment(GetbadgesById?.expiry_date).format("YYYY-MM-DD")
      );
      setUploadedData(GetbadgesById?.image)
    }
  }, [id, GetbadgesById]);



  const onSubmit: SubmitHandler<ChallengesFormData> = async (data) => {
    let imageUrl;

    if (uploadedData instanceof File) {
      // It's a new file, so upload it
      imageUrl = await uploadImage(uploadedData, "rewards");
    } else if (
      typeof uploadedData === "string" &&
      (uploadedData.startsWith("http") || !uploadedData.startsWith("https://"))
    ) {
      // It's already a URL, use it directly
      imageUrl = uploadedData;
    }
    mutate(
      {
        ...(data.type === "BADGE"
          ? { discount: 0 }
          : data.discount
          ? { discount: data.discount }
          : {}),
        ...(data.type && { type: data?.type }),
        ...(data.description && { description: data?.description }),
        ...(data?.code && { coupon_code: data?.code }),
        ...(data?.points && { target_points: data?.points }),
        ...(data?.expiryDate && { expiry_date: data?.expiryDate }),
        ...((imageUrl || uploadedData) && { image: imageUrl || uploadedData }),
      } as any,
      {
        onSuccess: (data: any) => {
          toast.success(data?.message);
          navigate(`/badges`);
        },
        onError: (error: any) => {
          toast.error(error.response.data.message);
        },
      }
    );
  };
  const optionType = [
    { value: "DISCOUNT", label: "Discount" },
    { value: "BADGE", label: "Badge" },
  ];

  const breadcrumbItems = [
    { title: "Badges", link: "badges" },
    { title: "Edit " },
  ];
  return (
    <Sidebar>
      <div className="mt-3">
        <CustomBreadcrumb items={breadcrumbItems} />
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 mt-6">
          <div>
            <p className="text-gray-700 block mb-2">Type</p>
            <Controller
              name={`type`}
              control={control}
              rules={{ required: "Type is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={optionType}
                  value={optionType.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(selectedOption) =>
                    field.onChange(selectedOption?.value)
                  }
                  placeholder="Select type"
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: "44px", // Background color for the select control
                      boxShadow: "none",
                      caretColor: "#24B1B3",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      fontSize: "14px",
                      "&:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                        borderColor: "gray !important",
                      },
                      "&:hover": { borderColor: "none !important" },
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "rgb(148 163 184)", // Set placeholder color here
                    }),
                  }}
                />
              )}
            />
            {errors && (
              <p className="text-red-500 text-sm mt-1">
                {errors.type?.message as string}
              </p>
            )}
          </div>
          {(selectedType === "DISCOUNT" || selectedType === undefined) && (
            <InputField
              label="Coupon Code"
              placeholder="Enter Coupon Code"
              type="text"
              register={register("code", {
                required:
                  selectedType === "DISCOUNT" || selectedType === undefined
                    ? "Coupon Code is required"
                    : false,
              })}
              error={errors.code?.message}
            />
          )}
          {(selectedType === "DISCOUNT" || selectedType === undefined) && (
            <InputField
              label="Discount Percentege"
              placeholder="Enter Percentege  1 to 100"
              type="number"
              // name="discount"
              register={register("discount", {
                required:
                  selectedType === "DISCOUNT" || selectedType === undefined
                    ? "Discount is required"
                    : false,
                valueAsNumber: true,
                validate: (value: number) => {
                  if (value <= 0) return "Discount must be positive";
                  if (value > 100) return "Discount cannot exceed 100";
                  if (!Number.isInteger(value))
                    return "Discount must be a whole number";
                  return true;
                },
              })}
              error={errors?.discount?.message}
            />
          )}
          <InputField
            label="Target Points"
            placeholder="Enter Target Points"
            type="number"
            register={register("points", {
              required: "Target Points is required",
            })}
            error={errors.points?.message}
          />
          <InputField
            label="Expiry Date"
            type="date"
            register={register("expiryDate", {
              required: "expiry date is required",
              validate: (value: any) =>
                value >= today || "expiry date cannot be in the past",
            })}
            error={errors.expiryDate?.message}
          />
          {/* <InputField
            label="End Date"
            type="date"
            register={register("endDate", {
              required: "end date is required",
              validate: (value) => {
                const startDate = watch("startDate");
                return (
                  !startDate ||
                  value >= startDate ||
                  "End date cannot be before start date"
                );
              },
            })}
            error={errors.endDate?.message}
          /> */}
          {/* <div>
            <p className="text-gray-700 block mb-2">Type</p>
            <Controller
              name={`type`}
              control={control}
              rules={{ required: "type is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={optionType}
                  value={optionType.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(selectedOption) =>
                    field.onChange(selectedOption?.value)
                  }
                  placeholder="Select type"
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: "44px", // Background color for the select control
                      boxShadow: "none",
                      caretColor: "#24B1B3",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      fontSize: "14px",
                      "&:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                        borderColor: "gray !important",
                      },
                      "&:hover": { borderColor: "none !important" },
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "rgb(148 163 184)", // Set placeholder color here
                    }),
                  }}
                />
              )}
            />
            {errors && (
              <p className="text-red-500 text-sm mt-1">
                {errors.type?.message as string}
              </p>
            )}
          </div> */}
          {/* <div>
            <p className="text-gray-700 block mb-2">Category</p>
            <Controller
              name="category"
              control={control}
              rules={{ required: "Category is required" }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={ChallengesCategoryOption}
                  value={ChallengesCategoryOption.find(
                    (option) => option.value === field.value
                  )}
                  onChange={(selectedOption) =>
                    field.onChange(selectedOption?.value)
                  }
                  placeholder="Select category"
                  styles={{
                    control: (base) => ({
                      ...base,
                      height: "44px", // Background color for the select control
                      boxShadow: "none",
                      caretColor: "#24B1B3",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      fontSize: "14px",
                      "&:focus": {
                        outline: "none !important",
                        boxShadow: "none !important",
                        borderColor: "gray !important",
                      },
                      "&:hover": { borderColor: "none !important" },
                    }),
                    placeholder: (base) => ({
                      ...base,
                      color: "rgb(148 163 184)", // Set placeholder color here
                    }),
                  }}
                />
              )}
            />
            {errors.category && (
              <p className="text-red-500 text-sm mt-1">
                {errors.category.message}
              </p>
            )}
          </div> */}
          {/* {selectedCategory === "Weight_Lifting" && (
            <div>
              <p className="text-gray-700 block mb-2">WorkOuts</p>
              <Controller
                name={`workoutIds`}
                control={control}
                disabled={WorkoutLoading}
                rules={{
                  required:
                    selectedCategory === "Weight_Lifting"
                      ? "workout is required"
                      : false,
                }}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={WorkoutData?.data.map((workout) => ({
                      value: workout.id.toString(),
                      label: workout.title,
                    }))}
                    isMulti
                    styles={{
                      control: (base) => ({
                        ...base,
                        minHeight: "44px", // Background color for the select control
                        boxShadow: "none",
                        caretColor: "#24B1B3",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "&:focus": {
                          outline: "none !important",
                          boxShadow: "none !important",
                          borderColor: "gray !important",
                        },
                        "&:hover": { borderColor: "none !important" },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "rgb(148 163 184)", // Set placeholder color here
                      }),
                    }}
                    placeholder="Select workout"
                  />
                )}
              />
              {errors && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.workoutIds?.message}
                </p>
              )}
            </div>
          )} */}
        </div>
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2 mt-4">
          <div className="">
            <p className="text-gray-700 block mb-2">Description</p>
            <Controller
              name="description"
              control={control}
              rules={{ required: "description is required" }}
              render={({ field }) => (
                <ReactQuill
                  {...field}
                  // onChange={(value) => {
                  //   field.onChange(value);
                  // }}
                  className="quill-editor"
                  modules={{
                    toolbar: [
                      ["bold", "italic", "underline", "strike"],
                      ["blockquote", "code-block"],
                      [{ header: 1 }, { header: 2 }],
                      [{ list: "ordered" }, { list: "bullet" }],

                      [{ size: ["small", false, "large", "huge"] }],
                      [{ header: [1, 2, 3, 4, 5, 6, false] }],
                      [{ color: [] }, { background: [] }],

                      [{ align: [] }],
                      ["clean"],
                    ],
                  }}
                />
              )}
            />
            {errors.description && (
              <p className="text-red-500 text-sm mt-1">
                {errors.description.message}
              </p>
            )}
          </div>
          <div>
            <p className="text-gray-700 block mb-2">Upload Image</p>

            <div className="mt-3">
              <label htmlFor="file">
                <div
                  role="button"
                  className="flex flex-col border-[1.5px] border-dashed border-gray-400 justify-center rounded-2xl items-center min-h-52"
                >
                  {uploadedData ? (
                    <>
                      {/* // Display uploaded URL if present */}
                      <img src={uploadedData} width={"100%"} alt="" />
                      <p className="text-center text-gray-600 text-sm">
                        {uploadedData.name}
                      </p>
                    </>
                  ) : (
                    // Display upload prompt if nothing is uploaded
                    <>
                      <FileUploadIcon />
                      <CustomButton
                        btntext="Upload"
                        addclass="mt-3"
                        handleClick={() =>
                          document.getElementById("file")?.click()
                        }
                      />
                    </>
                  )}
                </div>
              </label>
              <input
                type="file"
                className="hidden"
                id="file"
                accept={"image/jpeg, image/png, image/jpg"}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setUploadedData(e.target.files[0]);
                  }
                }}
              />
            </div>
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">
                {(errors.image as FieldError)?.message}
              </p>
            )}
          </div>
          {/* <div>
            <p className="text-gray-700 block mb-2">Upload Image</p>

            <div className="mt-3">
              <label htmlFor="file">
                <div
                  role="button"
                  className="flex flex-col border-[1.5px] border-dashed border-gray-400 justify-center rounded-2xl items-center min-h-52"
                >
                  {uploadedData ? (
                    // Display uploaded URL if present
                    <p className="text-center text-gray-600 text-sm">
                      {uploadedData.name}
                    </p>
                  ) : (
                    // Display upload prompt if nothing is uploaded
                    <>
                      <FileUploadIcon />
                      <CustomButton
                        btntext="Upload"
                        addclass="mt-3"
                        handleClick={() =>
                          document.getElementById("file")?.click()
                        }
                      />
                    </>
                  )}
                </div>
              </label>
              <input
                type="file"
                className="hidden"
                id="file"
                accept={"image/jpeg, image/png, image/jpg"}
                onChange={(e) => {
                  if (e.target.files?.[0]) {
                    setUploadedData(e.target.files[0]);
                  }
                }}
              />
            </div>
            {errors.image && (
              <p className="text-red-500 text-sm mt-1">
                {(errors.image as FieldError)?.message}
              </p>
            )}
          </div> */}
        </div>

        <CustomButton
          type="submit"
          btntext="Submit"
          isLoading={isPending || isUploading}
          addclass="mt-9 mx-auto w-1/2 h-fit"
        />
        {/* </div> */}
      </form>
    </Sidebar>
  );
};

export default UpdateBadges;
